import type {Canvas, StaticCanvas} from '@postermywall/fabricjs-2';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {AnimationType, ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {AnimateItemType, getAnimationTime, PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR, MAX_ITEM_ANIMATION_DELAY_FACTOR, MAX_ITEMS_WITH_FIXED_DELAY_FACTOR} from '@PosterWhiteboard/page/page.types';

const DELAY_FACTOR_ZERO = 0;
const DELAY_FACTOR_VECTOR = 0.1;
const DELAY_FACTOR_IMAGE = 0.2;
const OUTRO_DELAY_FACTOR = 0.2;

interface IntroStartTimesResult {
  startTimes: Record<string, number>;
  directionUpdates: Record<string, PositionType>;
}

interface OutroEndTimesResult {
  endTimes: Record<string, number>;
  directionUpdates: Record<string, PositionType>;
}

export const isAnimationDurationApplicableOnItem = (item: ItemType): boolean => {
  return !item.isSlideshowSlide() && !item.isTranscript() && !item.isImageBackground();
};

export const itemHasDifferentStartTime = (item: ItemType): boolean => {
  return item.isTranscript() || (item.isSlideshow() && item.hasIntroDelay());
};

export const itemHasDifferentEndTime = (item: ItemType): boolean => {
  if (item.isTranscript()) {
    return true;
  }
  if (!item.isSlideshow()) {
    return false;
  }
  return !(item.hasSingleSlide() && !item.hasIntroDelay());
};

const getSortedItemsByPosition = (items: ItemType[], animateType: AnimateItemType): ItemType[] => {
  const filteredItems = items.filter((item) => {
    const type = animateType === AnimateItemType.INTRO_ANIMATION ? item.introAnimation.type : item.outroAnimation.type;
    const isAnimationApplicable = [AnimationType.RISE, AnimationType.BLOCK, AnimationType.PAN, AnimationType.TUMBLE, AnimationType.PEEK, AnimationType.WIPE].includes(type);
    return isAnimationApplicable && (animateType === AnimateItemType.INTRO_ANIMATION ? !itemHasDifferentStartTime(item) : !itemHasDifferentEndTime(item));
  });

  return filteredItems.sort((a, b) => {
    const boundTopA = a.fabricObject.getBoundingRect().top;
    const boundLeftA = a.fabricObject.getBoundingRect().left;
    const boundTopB = b.fabricObject.getBoundingRect().top;
    const boundLeftB = b.fabricObject.getBoundingRect().left;
    return boundTopA !== boundTopB ? boundTopA - boundTopB : boundLeftA - boundLeftB;
  });
};

const getSortedItemsByZIndex = (items: ItemType[], fabricCanvas: Canvas | StaticCanvas, animateType: AnimateItemType): ItemType[] => {
  const filteredItems = items.filter((item) => {
    const type = animateType === AnimateItemType.INTRO_ANIMATION ? item.introAnimation.type : item.outroAnimation.type;
    const isAnimationApplicable = [AnimationType.POP, AnimationType.JELLO, AnimationType.ROTATE].includes(type);
    return isAnimationApplicable && (animateType === AnimateItemType.INTRO_ANIMATION ? !itemHasDifferentStartTime(item) : !itemHasDifferentEndTime(item));
  });

  const fabricObjectsInCanvas = fabricCanvas.getObjects();
  return filteredItems.sort((a, b) => {
    return fabricObjectsInCanvas.indexOf(a.fabricObject) - fabricObjectsInCanvas.indexOf(b.fabricObject);
  });
};

const getDelayForModelType = (itemType: ITEM_TYPE, speed: number): number => {
  switch (itemType) {
    case ITEM_TYPE.VECTOR:
      return speed * DELAY_FACTOR_VECTOR;
    case ITEM_TYPE.IMAGE:
    case ITEM_TYPE.DRAWING:
      return speed * DELAY_FACTOR_IMAGE;
    case ITEM_TYPE.TEXT:
    case ITEM_TYPE.FANCY_TEXT:
    case ITEM_TYPE.TAB:
    case ITEM_TYPE.TABLE:
    case ITEM_TYPE.MENU:
    case ITEM_TYPE.SLIDESHOW:
      return speed * MAX_ITEM_ANIMATION_DELAY_FACTOR;
    default:
      return DELAY_FACTOR_ZERO;
  }
};

const getDelayFactorByTotalObjects = (totalItems: number, speed: number): number => {
  const totalObjectsWithDelay = totalItems - 1;
  return totalObjectsWithDelay > 0 ? (MAX_ITEM_ANIMATION_DELAY_FACTOR * speed) / totalObjectsWithDelay : 0;
};

const getAlternativeDelayFactor = (totalItems: number, speed: number): number => {
  if (speed) {
    if (totalItems <= MAX_ITEMS_WITH_FIXED_DELAY_FACTOR + 1) {
      return speed * FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR;
    }
    return (speed * FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR * MAX_ITEMS_WITH_FIXED_DELAY_FACTOR) / (totalItems - 1);
  }
  return 0;
};

const getTumbleAnimationDirection = (item: ItemType, fabricCanvas: Canvas | StaticCanvas): PositionType => {
  const itemZIndex = fabricCanvas.getObjects().indexOf(item.fabricObject);
  switch (itemZIndex % 8) {
    case 0:
      return PositionType.TOP_LEFT;
    case 1:
      return PositionType.BOTTOM;
    case 2:
      return PositionType.RIGHT;
    case 3:
      return PositionType.BOTTOM_RIGHT;
    case 4:
      return PositionType.TOP;
    case 5:
      return PositionType.LEFT;
    case 6:
      return PositionType.TOP_RIGHT;
    default:
      return PositionType.BOTTOM_LEFT;
  }
};

export const calculateIntroStartTimes = (
  itemsHashMap: Record<string, ItemType>,
  fabricCanvas: Canvas | StaticCanvas,
  pageDuration: number,
  isVideoBackground: boolean
): IntroStartTimesResult => {
  const startTimes: Record<string, number> = {};
  const directionUpdates: Record<string, PositionType> = {};
  const items = Object.values(itemsHashMap);

  for (const currentItem of items) {
    if (itemHasDifferentStartTime(currentItem)) {
      startTimes[currentItem.uid] = currentItem.onPageStartTime;
      continue;
    }

    const animationSpeed = getAnimationTime(currentItem.introAnimation.type, currentItem.introAnimation.speed);

    switch (currentItem.introAnimation.type) {
      case AnimationType.SLIDE:
        startTimes[currentItem.uid] = isVideoBackground ? getDelayForModelType(currentItem.gitype, animationSpeed) : 0;
        break;

      case AnimationType.RISE: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.INTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        startTimes[currentItem.uid] = index >= 0 ? getDelayFactorByTotalObjects(items.length, animationSpeed) * index : 0;
        break;
      }

      case AnimationType.BLOCK: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.INTRO_ANIMATION).filter((item) => item.isText() || item.isSlideshow());
        if (sortedItems.length) {
          const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
          startTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        }
        break;
      }

      case AnimationType.TUMBLE: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.INTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        startTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        directionUpdates[currentItem.uid] = getTumbleAnimationDirection(currentItem, fabricCanvas);
        break;
      }

      case AnimationType.PEEK:
      case AnimationType.WIPE:
      case AnimationType.PAN: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.INTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        startTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        break;
      }

      case AnimationType.POP:
      case AnimationType.JELLO: {
        const sortedItems = getSortedItemsByZIndex(items, fabricCanvas, AnimateItemType.INTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        startTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        break;
      }

      case AnimationType.ROTATE: {
        const sortedItems = getSortedItemsByZIndex(items, fabricCanvas, AnimateItemType.INTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        startTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        break;
      }

      case AnimationType.BOUNCE:
      case AnimationType.BOUNCE_IN_DOWN:
      case AnimationType.FADE:
        startTimes[currentItem.uid] = getDelayForModelType(currentItem.gitype, animationSpeed);
        break;

      case AnimationType.NONE:
      case AnimationType.SCALE:
      case AnimationType.ZOOM:
      default:
        startTimes[currentItem.uid] = 0;
        break;
    }
  }

  return {startTimes, directionUpdates};
};

export const calculateOutroEndTimes = (itemsHashMap: Record<string, ItemType>, fabricCanvas: Canvas | StaticCanvas, pageDuration: number): OutroEndTimesResult => {
  const endTimes: Record<string, number> = {};
  const directionUpdates: Record<string, PositionType> = {};
  const items = Object.values(itemsHashMap);
  let maxAnimationDuration = 0;

  for (const currentItem of items) {
    const animationSpeed = getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed);

    switch (currentItem.outroAnimation.type) {
      case AnimationType.RISE: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.OUTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        endTimes[currentItem.uid] = index >= 0 ? getDelayFactorByTotalObjects(items.length, animationSpeed) * index : 0;
        break;
      }

      case AnimationType.BLOCK: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.OUTRO_ANIMATION).filter((item) => item.isText() || item.isSlideshow());
        if (sortedItems.length) {
          const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
          endTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index - animationSpeed * 0.5 : 0;
        }
        break;
      }

      case AnimationType.TUMBLE: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.OUTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        endTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        directionUpdates[currentItem.uid] = getTumbleAnimationDirection(currentItem, fabricCanvas);
        break;
      }

      case AnimationType.PEEK:
      case AnimationType.WIPE:
      case AnimationType.PAN: {
        const sortedItems = getSortedItemsByPosition(items, AnimateItemType.OUTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        endTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        break;
      }

      case AnimationType.POP:
      case AnimationType.JELLO: {
        const sortedItems = getSortedItemsByZIndex(items, fabricCanvas, AnimateItemType.OUTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        endTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        break;
      }

      case AnimationType.ROTATE: {
        const sortedItems = getSortedItemsByZIndex(items, fabricCanvas, AnimateItemType.OUTRO_ANIMATION);
        const index = sortedItems.findIndex((item) => currentItem.uid === item.uid);
        endTimes[currentItem.uid] = index >= 0 ? getAlternativeDelayFactor(sortedItems.length, animationSpeed) * index : 0;
        break;
      }

      default:
        endTimes[currentItem.uid] = 0;
    }

    maxAnimationDuration = Math.max(maxAnimationDuration, endTimes[currentItem.uid] ?? 0);
    const itemPageDuration = itemHasDifferentEndTime(currentItem) ? currentItem.getDuration() : pageDuration;
    endTimes[currentItem.uid] = (endTimes[currentItem.uid] ?? 0) + itemPageDuration;
  }

  for (const [itemId, endTime] of Object.entries(endTimes)) {
    const item = itemsHashMap[itemId];
    if (item.hasOutroAnimation()) {
      const itemPageDuration = itemHasDifferentEndTime(item) ? 0 : maxAnimationDuration;
      endTimes[itemId] = endTime - itemPageDuration;
      if (endTimes[itemId] === pageDuration) {
        endTimes[itemId] -= OUTRO_DELAY_FACTOR;
      }
    }
  }

  return {endTimes, directionUpdates};
};
